import {
  type QueryKey,
  useMutation,
  type UseMutationOptions,
  useQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";

import type { ErrorType } from "@/lib/api-client";

// Small helpers that give the hand-written API modules the same call shapes the
// orval-generated client had, so components did not need to change.

export type RequestOptions = AxiosRequestConfig;

export interface QueryHookOptions<TData> {
  query?: Partial<UseQueryOptions<TData, ErrorType<unknown>, TData>>;
  request?: RequestOptions;
}

export interface MutationHookOptions<TData, TVariables> {
  mutation?: UseMutationOptions<TData, ErrorType<unknown>, TVariables>;
  request?: RequestOptions;
}

export function useApiQuery<TData>(
  queryKey: QueryKey,
  queryFn: (
    request: RequestOptions | undefined,
    signal: AbortSignal,
  ) => Promise<TData>,
  options?: QueryHookOptions<TData>,
) {
  return useQuery<TData, ErrorType<unknown>, TData>({
    queryKey,
    queryFn: ({ signal }) => queryFn(options?.request, signal),
    ...options?.query,
  });
}

export function useApiMutation<TData, TVariables>(
  mutationFn: (
    variables: TVariables,
    request: RequestOptions | undefined,
  ) => Promise<TData>,
  options?: MutationHookOptions<TData, TVariables>,
) {
  return useMutation<TData, ErrorType<unknown>, TVariables>({
    mutationFn: (variables) => mutationFn(variables, options?.request),
    ...options?.mutation,
  });
}
